import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { getProject, projects } from '../data/projects';
import { EndNote } from '../components/EndNote';
type TabId = 'challenge' | 'approach' | 'outcome';
const tabs: {
  id: TabId;
  label: string;
}[] = [{
  id: 'challenge',
  label: 'Challenge'
}, {
  id: 'approach',
  label: 'Approach'
}, {
  id: 'outcome',
  label: 'Outcome'
}];
export function CaseStudy() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const project = slug ? getProject(slug) : undefined;
  const [tab, setTab] = useState<TabId>('challenge');
  if (!project) {
    return <div className="mx-auto max-w-page px-6 py-24 md:px-10" data-id="element-82" data-mp="37l7csz7yyfok">
        <h1 className="font-display text-4xl font-bold uppercase leading-[0.9] md:text-6xl" data-id="element-83" data-mp="37lh36nwxeous">
          We can&apos;t find
          <br data-id="element-84" data-mp="10sy745gy51rb" />
          that project
        </h1>
        <Link to="/work" className="mt-7 inline-flex items-center gap-2 border-b-2 border-lilac pb-1 text-[0.7rem] uppercase tracking-micro transition-colors hover:text-lilac" data-id="element-85" data-mp="15ssx86swdnbb">
          <ArrowLeftIcon size={14} data-id="element-86" data-mp="3754kpdwmtpbh" />
          Back to work
        </Link>
      </div>;
  }
  const index = projects.findIndex(item => item.slug === project.slug);
  const next = projects[(index + 1) % projects.length];
  return <article data-id="element-87" data-mp="3gxtpxr1la6yd">
      <div className="mx-auto max-w-page px-6 pt-10 md:px-10 md:pt-14" data-id="element-88" data-mp="2t64vo557j8es">
        <Link to="/work" className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-micro text-muted transition-colors hover:text-sage" data-id="element-89" data-mp="2t6ejbm2s53hb">
          <ArrowLeftIcon size={14} data-id="element-90" data-mp="328e4mqktutq2" />
          All work
        </Link>

        <div className="mt-6 flex flex-wrap items-end justify-between gap-6" data-id="element-91" data-mp="2x7globrztvni">
          <h1 className="font-display text-4xl font-bold uppercase leading-[0.9] md:text-7xl" data-id="element-92" data-mp="hgc0tgfy7xat">
            {project.title}
          </h1>
          <p className="max-w-sm leading-relaxed text-muted" data-id="element-93" data-mp="cgpn75w7kf1h">
            {project.summary}
          </p>
        </div>

        {project.video ? <video src={project.video} poster={project.image || undefined} controls autoPlay loop muted playsInline aria-label={`${project.title} — ${project.client}`} className="mt-8 h-[42vh] w-full bg-cream/5 object-cover md:h-[62vh]" data-id="element-94" data-mp="3b8jzdfivcyyb" /> : <img src={project.image} alt={`${project.title} — ${project.client}`} className="mt-8 h-[42vh] w-full object-cover md:h-[62vh]" data-id="element-95" data-mp="2nku1qc1wfvtn" />}

        <dl className="mt-8 grid grid-cols-2 gap-6 border-t border-line pt-5 md:grid-cols-4" data-id="element-96" data-mp="2wicrtuueh0ce">
          {[{
          label: 'Client',
          value: project.client
        }, {
          label: 'Location',
          value: project.location
        }, {
          label: 'Year',
          value: project.year
        }, {
          label: 'Discipline',
          value: project.discipline
        }].map(item => <div key={item.label} data-id="element-97" data-mp="219ixjd66jubf">
              <dt className="text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-98" data-mp="219sn4znbbzgl">
                {item.label}
              </dt>
              <dd className="mt-1.5 font-display text-lg font-semibold uppercase md:text-xl" data-id="element-99" data-mp="1rcec5fk5s2vo">
                {item.value}
              </dd>
            </div>)}
        </dl>
      </div>

      <div className="mx-auto max-w-page px-6 py-14 md:px-10 md:py-20" data-id="element-100" data-mp="1ibpvse7alqfm">
        <div className="grid gap-8 md:grid-cols-12" data-id="element-101" data-mp="1id11ijkat89d">
          <div className="md:col-span-3" data-id="element-102" data-mp="1dkpwx3em4yah">
            <div role="tablist" aria-label="Case study sections" className="flex gap-2 md:flex-col md:items-start" data-id="element-103" data-mp="1djojisa4jzpl">
              {tabs.map(item => {
              const active = tab === item.id;
              return <button key={item.id} role="tab" id={`tab-${item.id}`} aria-selected={active} aria-controls={`panel-${item.id}`} type="button" onClick={() => setTab(item.id)} className={`rounded-full px-4 py-2 text-[0.65rem] uppercase tracking-micro transition-colors ${active ? 'bg-sage text-ink' : 'text-muted hover:text-cream'}`} data-id="element-104" data-mp="zkoydultm2y6">
                    {item.label}
                  </button>;
            })}
            </div>

            <ul className="mt-8 space-y-1.5" data-id="element-105" data-mp="2fxe1idc8b7bq">
              {project.scope.map(item => <li key={item} className="flex gap-2 text-sm text-cream/80" data-id="element-106" data-mp="221k2cm19bwbt">
                  <span className="text-lilac" data-id="element-107" data-mp="26zlndci8u5px">✳</span>
                  {item}
                </li>)}
            </ul>
          </div>

          <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`} className="md:col-span-8 md:col-start-5" data-id="element-108" data-mp="57fic5bto6kp">
            <p className="text-lg leading-relaxed md:text-2xl" data-id="element-109" data-mp="f99oasexuw34">
              {project[tab]}
            </p>
          </div>
        </div>
      </div>

      {project.gallery && <section className="mx-auto max-w-page px-6 pb-14 md:px-10 md:pb-20" data-id="element-110" data-mp="o592ewstgbpw">
          <div className="grid gap-3 sm:grid-cols-2" data-id="element-111" data-mp="jbwll8udp97p">
            {project.gallery.map(src => <img key={src} src={src} alt="" aria-hidden="true" className="h-56 w-full object-cover md:h-80" data-id="element-112" data-mp="y0kgdcwehewl" />)}
          </div>
        </section>}

      {project.stats && <section className="bg-sage text-ink" data-id="element-113" data-mp="130ovayhnu1kp">
        <div className="mx-auto grid max-w-page gap-8 px-6 py-12 md:grid-cols-3 md:px-10" data-id="element-114" data-mp="12yuarlaawbh5">
          {project.stats.map(stat => <div key={stat.label} data-id="element-115" data-mp="34stmg7rc4uud">
              <p className="font-display text-4xl font-bold uppercase leading-none md:text-5xl" data-id="element-116" data-mp="34tmtl9u7hmd1">
                {stat.value}
              </p>
              <p className="mt-2 text-[0.6rem] uppercase tracking-micro text-ink/60" data-id="element-117" data-mp="1cqsbngu3kemm">
                {stat.label}
              </p>
            </div>)}
        </div>
      </section>}

      {project.quote && <section className="mx-auto max-w-page px-6 py-16 md:px-10 md:py-24" data-id="element-118" data-mp="1mp82rjt4jnw4">
          <blockquote className="mx-auto max-w-3xl text-center" data-id="element-119" data-mp="1qqtayruprdzz">
            <p className="font-display text-2xl font-medium uppercase leading-[1.1] md:text-4xl" data-id="element-120" data-mp="1qq9vdhsjd1p4">
              “{project.quote.text}”
            </p>
            <footer className="mt-6 text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-121" data-mp="20g33g8tgyyom">
              {project.quote.attribution}
            </footer>
          </blockquote>
        </section>}

      <section className="border-t border-line" data-id="element-122" data-mp="2afbuast1fx6r">
        <Link to={`/work/${next.slug}`} className="group mx-auto flex max-w-page items-center justify-between gap-8 px-6 py-12 md:px-10 md:py-16" data-id="element-123" data-mp="2acfwd4h6x8ib">
          <div data-id="element-124" data-mp="2tk8a6hvanwn">
            <span className="text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-125" data-mp="2u3p1jvs7693">
              Next project
            </span>
            <p className="mt-2 font-display text-3xl font-bold uppercase leading-none transition-colors group-hover:text-sage md:text-5xl" data-id="element-126" data-mp="3pxgtzsif371g">
              {next.title}
            </p>
          </div>
          <ArrowRightIcon size={26} className="shrink-0 transition-transform duration-300 group-hover:translate-x-2" data-id="element-127" data-mp="mgruurl5yzlt" />
        </Link>
      </section>

      <EndNote data-id="element-128" data-mp="hjrmub4r7ms2" />
    </article>;
}